/**
 * Sección "Contraseña".
 *
 * Dos modos según el estado del usuario:
 *   - has_password=true  → cambiar contraseña (current + new + confirm)
 *   - has_password=false → primer set (new + confirm + TOTP si MFA on)
 *
 * Tras éxito en cambio se muestra cuántas otras sesiones fueron revocadas.
 */

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Eye, EyeOff } from 'lucide-react'

import {
  ActionButton,
  FieldRow,
  InlineAlert,
  SettingsSection,
  TextInput,
} from '@/components/ui/settings-primitives'
import { useChangePassword, useSetPassword } from '@/hooks/api/use-profile'
import type { ProfileResponse } from '@/lib/api'

export function PasswordSection({ profile }: { profile: ProfileResponse }) {
  return profile.has_password ? (
    <ChangePasswordForm />
  ) : (
    <SetPasswordForm totpEnabled={profile.totp_enabled} />
  )
}

// ─── Change (rotación) ─────────────────────────────────────────────

function ChangePasswordForm() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)

  const change = useChangePassword()
  const strength = useMemo(() => evaluateStrength(next), [next])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (next !== confirm) {
      toast.error('La confirmación no coincide')
      return
    }
    if (strength.score < 3) {
      toast.error('La contraseña es demasiado débil')
      return
    }
    try {
      const resp = await change.mutateAsync({ current_password: current, new_password: next })
      toast.success(resp.message)
      setCurrent('')
      setNext('')
      setConfirm('')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos cambiar la contraseña')
    }
  }

  return (
    <SettingsSection
      title="Cambiar contraseña"
      description="Al cambiarla cerraremos automáticamente todas las sesiones en otros dispositivos."
      footer={
        <ActionButton
          type="submit"
          form="password-form"
          disabled={!current || !next || !confirm}
          loading={change.isPending}
        >
          Actualizar contraseña
        </ActionButton>
      }
    >
      <form id="password-form" onSubmit={onSubmit} className="space-y-4">
        <FieldRow label="Contraseña actual">
          <PasswordInput
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            autoComplete="current-password"
          />
        </FieldRow>
        <FieldRow
          label="Nueva contraseña"
          hint="Mínimo 12 caracteres. Combina mayúsculas, minúsculas, números y símbolos."
        >
          <PasswordInput
            value={next}
            onChange={setNext}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            autoComplete="new-password"
            minLength={12}
          />
          <StrengthBar score={strength.score} label={strength.label} color={strength.color} />
        </FieldRow>
        <FieldRow label="Confirma la nueva contraseña">
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            autoComplete="new-password"
          />
          {confirm && confirm !== next && (
            <p className="mt-1 text-xs text-error-fg">No coincide.</p>
          )}
        </FieldRow>
      </form>
    </SettingsSection>
  )
}

// ─── Set (primera vez, OAuth-only users) ───────────────────────────

function SetPasswordForm({ totpEnabled }: { totpEnabled: boolean }) {
  const [pwd, setPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [totp, setTotp] = useState('')
  const [show, setShow] = useState(false)

  const set = useSetPassword()
  const strength = useMemo(() => evaluateStrength(pwd), [pwd])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (pwd !== confirm) {
      toast.error('La confirmación no coincide')
      return
    }
    if (strength.score < 3) {
      toast.error('La contraseña es demasiado débil')
      return
    }
    try {
      const resp = await set.mutateAsync({
        new_password: pwd,
        totp_code: totpEnabled ? totp.trim() : null,
      })
      toast.success(resp.message)
      setPwd('')
      setConfirm('')
      setTotp('')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos configurar la contraseña')
    }
  }

  return (
    <SettingsSection
      title="Configurar contraseña"
      description="Tu cuenta usa solo proveedores OAuth. Al configurar una contraseña podrás iniciar sesión también con correo + contraseña."
      footer={
        <ActionButton
          type="submit"
          form="set-password-form"
          disabled={
            !pwd || !confirm || (totpEnabled && totp.length < 6)
          }
          loading={set.isPending}
        >
          Configurar contraseña
        </ActionButton>
      }
    >
      <form id="set-password-form" onSubmit={onSubmit} className="space-y-4">
        <InlineAlert variant="info">
          Tener una contraseña además de OAuth te permite recuperar acceso si pierdes la
          cuenta del proveedor (Google/Microsoft). También es requisito para cambiar correo
          o teléfono.
        </InlineAlert>
        <FieldRow
          label="Nueva contraseña"
          hint="Mínimo 12 caracteres. Combina mayúsculas, minúsculas, números y símbolos."
        >
          <PasswordInput
            value={pwd}
            onChange={setPwd}
            show={show}
            onToggle={() => setShow((v) => !v)}
            autoComplete="new-password"
            minLength={12}
          />
          <StrengthBar score={strength.score} label={strength.label} color={strength.color} />
        </FieldRow>
        <FieldRow label="Confirma la contraseña">
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            show={show}
            onToggle={() => setShow((v) => !v)}
            autoComplete="new-password"
          />
          {confirm && confirm !== pwd && (
            <p className="mt-1 text-xs text-error-fg">No coincide.</p>
          )}
        </FieldRow>
        {totpEnabled && (
          <FieldRow
            label="Código TOTP"
            hint="Tu cuenta tiene MFA activo. Confirma con un código de tu app."
          >
            <TextInput
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              required
            />
          </FieldRow>
        )}
      </form>
    </SettingsSection>
  )
}

// ─── Reusable bits ────────────────────────────────────────────────

function PasswordInput({
  value,
  onChange,
  show,
  onToggle,
  autoComplete,
  minLength,
}: {
  value: string
  onChange: (v: string) => void
  show: boolean
  onToggle: () => void
  autoComplete: string
  minLength?: number
}) {
  return (
    <div className="relative">
      <TextInput
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        minLength={minLength}
        required
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-fg-muted hover:text-fg-secondary"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

function StrengthBar({
  score,
  label,
  color,
}: {
  score: number
  label: string
  color: string
}) {
  if (score === 0 && !label) return null
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${s <= score ? color : 'bg-bg-elevated'}`}
          />
        ))}
      </div>
      <p className="mt-1 text-xs text-fg-muted">{label}</p>
    </div>
  )
}

function evaluateStrength(pwd: string): { score: number; label: string; color: string } {
  if (!pwd) return { score: 0, label: '', color: 'bg-bg-elevated' }
  let score = 0
  if (pwd.length >= 12) score++
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++
  if (/\d/.test(pwd)) score++
  if (/[^a-zA-Z0-9]/.test(pwd)) score++
  const labels = ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte']
  const colors = [
    'bg-error-fg/70',
    'bg-error-fg/70',
    'bg-warning-fg/70',
    'bg-success-fg/70',
    'bg-success-fg',
  ]
  return { score, label: labels[score], color: colors[score] }
}
